/**
 * Wishlist + share helpers (pure, no side effects outside localStorage).
 *
 * Privacy rules baked in here:
 *  - Shared links are the canonical public product URL only: no session token,
 *    no customer identity, no UTM, no quiz/health answers, no query string.
 *  - Guest wishlists live on the device only. Signed-in wishlists live in the
 *    database under the user's own row (RLS), never mixed with contact data.
 */

export const WISHLIST_STORAGE_KEY = "truwe_wishlist";
export const CANONICAL_ORIGIN = "https://truwe.in";

/** Canonical, shareable product URL. Strips any query/hash by construction. */
export function canonicalProductUrl(handle: string): string {
  const clean = String(handle || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
  return `${CANONICAL_ORIGIN}/products/${clean}`;
}

/** Share outcomes are deliberately distinct: opening a sheet is not a share. */
export type ShareOutcome =
  | "attempted" // user tapped share
  | "native_handoff" // OS share sheet accepted the payload — delivery NOT guaranteed
  | "link_copied" // link is on the clipboard
  | "dismissed" // user cancelled the sheet
  | "failed";

export function readGuestWishlist(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return dedupe(parsed.filter((v): v is string => typeof v === "string" && v.length > 0));
  } catch {
    return [];
  }
}

/** Returns false when device storage refused the write (full or blocked). */
export function writeGuestWishlist(handles: string[]): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(dedupe(handles)));
    return true;
  } catch {
    return false;
  }
}

/** Never store the same product twice, whichever path added it. */
export function dedupe(handles: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const h of handles) {
    const k = h.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

export const SAVED_PRODUCT_TITLES: Record<string, string> = {
  "mag-absorb-pro": "Mag Absorb Pro",
  "liver-biome-plus": "Liver Biome+",
  "ova-balance": "Ova Balance",
};

export function savedProductTitle(handle: string): string {
  return (
    SAVED_PRODUCT_TITLES[handle] ??
    handle
      .split("-")
      .map((p) => (p ? p[0]!.toUpperCase() + p.slice(1) : p))
      .join(" ")
  );
}
